import { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Alert,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Storage as StorageIcon,
  Memory as MemoryIcon,
  Speed as SpeedIcon,
  Timer as TimerIcon,
} from '@mui/icons-material';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  Filler,
} from 'chart.js';
import { format } from 'date-fns';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  ChartTooltip,
  Legend,
  Filler
);

interface ServiceHealth {
  name: string;
  status: 'healthy' | 'degraded' | 'down';
  responseTime: number;
  lastCheck: string;
  uptime: number;
  errorRate: number;
}

interface ShardStatus {
  id: string;
  status: 'healthy' | 'degraded' | 'down';
  userCount: number;
  activityCount: number;
  sizeMB: number;
  lastCheck: string;
}

interface CeleryWorker {
  name: string;
  status: 'active' | 'idle' | 'offline';
  tasksProcessed: number;
  currentTask: string | null;
  lastSeen: string;
}

interface SystemMetrics {
  timestamp: string;
  requestRate: number;
  responseTime: number;
  errorRate: number;
  cacheHitRate: number;
}

interface RedisMetrics {
  memoryUsedMB: number;
  memoryMaxMB: number;
  connectedClients: number;
  keysCount: number;
  hitRate: number;
}

const SystemHealthPage = () => {
  const [services, setServices] = useState<ServiceHealth[]>([
    {
      name: 'API Server',
      status: 'healthy',
      responseTime: 45,
      lastCheck: new Date().toISOString(),
      uptime: 99.98,
      errorRate: 0.02,
    },
    {
      name: 'Database Primary',
      status: 'healthy',
      responseTime: 12,
      lastCheck: new Date().toISOString(),
      uptime: 99.99,
      errorRate: 0.01,
    },
    {
      name: 'Redis Cache',
      status: 'healthy',
      responseTime: 3,
      lastCheck: new Date().toISOString(),
      uptime: 99.95,
      errorRate: 0.05,
    },
    {
      name: 'Celery Workers',
      status: 'healthy',
      responseTime: 150,
      lastCheck: new Date().toISOString(),
      uptime: 99.90,
      errorRate: 0.10,
    },
  ]);

  const [shards, setShards] = useState<ShardStatus[]>([
    {
      id: 'shard-001',
      status: 'healthy',
      userCount: 1250,
      activityCount: 45000,
      sizeMB: 2048,
      lastCheck: new Date().toISOString(),
    },
    {
      id: 'shard-002',
      status: 'healthy',
      userCount: 1180,
      activityCount: 42000,
      sizeMB: 1920,
      lastCheck: new Date().toISOString(),
    },
    {
      id: 'shard-003',
      status: 'degraded',
      userCount: 1420,
      activityCount: 51000,
      sizeMB: 2304,
      lastCheck: new Date().toISOString(),
    },
  ]);

  const [celeryWorkers, setCeleryWorkers] = useState<CeleryWorker[]>([
    {
      name: 'worker-1',
      status: 'active',
      tasksProcessed: 1523,
      currentTask: 'sync_strava_activities',
      lastSeen: new Date().toISOString(),
    },
    {
      name: 'worker-2',
      status: 'active',
      tasksProcessed: 1489,
      currentTask: 'calculate_training_load',
      lastSeen: new Date().toISOString(),
    },
    {
      name: 'worker-3',
      status: 'idle',
      tasksProcessed: 1501,
      currentTask: null,
      lastSeen: new Date().toISOString(),
    },
  ]);

  const [redisMetrics, setRedisMetrics] = useState<RedisMetrics>({
    memoryUsedMB: 512,
    memoryMaxMB: 2048,
    connectedClients: 45,
    keysCount: 125000,
    hitRate: 94.5,
  });

  const [metricsHistory, setMetricsHistory] = useState<SystemMetrics[]>([]);
  const [systemUptime, setSystemUptime] = useState<number>(0);
  const [isOffline, setIsOffline] = useState<boolean>(!navigator.onLine);
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);

  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Initialize metrics history
    const now = Date.now();
    const initialMetrics: SystemMetrics[] = Array.from({ length: 20 }, (_, i) => ({
      timestamp: new Date(now - (19 - i) * 60000).toISOString(),
      requestRate: Math.floor(Math.random() * 50) + 100,
      responseTime: Math.floor(Math.random() * 30) + 40,
      errorRate: Math.random() * 0.5,
      cacheHitRate: Math.random() * 5 + 90,
    }));
    setMetricsHistory(initialMetrics);

    // Simulate uptime counter
    const uptimeInterval = setInterval(() => {
      setSystemUptime((prev) => prev + 1);
    }, 1000);

    // Simulate real-time metrics updates
    const metricsInterval = setInterval(() => {
      setMetricsHistory((prev) => {
        const newMetric: SystemMetrics = {
          timestamp: new Date().toISOString(),
          requestRate: Math.floor(Math.random() * 50) + 100,
          responseTime: Math.floor(Math.random() * 30) + 40,
          errorRate: Math.random() * 0.5,
          cacheHitRate: Math.random() * 5 + 90,
        };
        return [...prev.slice(1), newMetric];
      });
    }, 60000);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      clearInterval(uptimeInterval);
      clearInterval(metricsInterval);
    };
  }, []);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    // Simulate API call
    await new Promise((resolve) => setTimeout(resolve, 1000));
    
    // Update last check times
    const now = new Date().toISOString();
    setServices((prev) =>
      prev.map((service) => ({ ...service, lastCheck: now }))
    );
    setShards((prev) =>
      prev.map((shard) => ({ ...shard, lastCheck: now }))
    );
    setCeleryWorkers((prev) =>
      prev.map((worker) => ({ ...worker, lastSeen: now }))
    );
    
    setIsRefreshing(false);
  };

  const getStatusIcon = (status: 'healthy' | 'degraded' | 'down' | 'active' | 'idle' | 'offline') => {
    switch (status) {
      case 'healthy':
      case 'active':
        return <CheckCircleIcon color="success" />;
      case 'degraded':
      case 'idle':
        return <WarningIcon color="warning" />;
      case 'down':
      case 'offline':
        return <ErrorIcon color="error" />;
      default:
        return null;
    }
  };

  const getStatusColor = (status: 'healthy' | 'degraded' | 'down' | 'active' | 'idle' | 'offline') => {
    switch (status) {
      case 'healthy':
      case 'active':
        return 'success';
      case 'degraded':
      case 'idle':
        return 'warning';
      case 'down':
      case 'offline':
        return 'error';
      default:
        return 'default';
    }
  };

  const formatUptime = (seconds: number): string => {
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${days}d ${hours}h ${minutes}m ${secs}s`;
  };

  const metricsChartData = {
    labels: metricsHistory.map((m) => format(new Date(m.timestamp), 'HH:mm')),
    datasets: [
      {
        label: 'Request Rate (req/s)',
        data: metricsHistory.map((m) => m.requestRate),
        borderColor: 'rgb(75, 192, 192)',
        backgroundColor: 'rgba(75, 192, 192, 0.2)',
        yAxisID: 'y',
      },
      {
        label: 'Response Time (ms)',
        data: metricsHistory.map((m) => m.responseTime),
        borderColor: 'rgb(255, 99, 132)',
        backgroundColor: 'rgba(255, 99, 132, 0.2)',
        yAxisID: 'y1',
      },
    ],
  };

  const metricsChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index' as const,
      intersect: false,
    },
    plugins: {
      legend: {
        position: 'top' as const,
      },
      title: {
        display: true,
        text: 'Real-time System Metrics',
      },
    },
    scales: {
      y: {
        type: 'linear' as const,
        display: true,
        position: 'left' as const,
        title: {
          display: true,
          text: 'Request Rate',
        },
      },
      y1: {
        type: 'linear' as const,
        display: true,
        position: 'right' as const,
        title: {
          display: true,
          text: 'Response Time (ms)',
        },
        grid: {
          drawOnChartArea: false,
        },
      },
    },
  };

  const errorRateChartData = {
    labels: metricsHistory.map((m) => format(new Date(m.timestamp), 'HH:mm')),
    datasets: [
      {
        label: 'Error Rate (%)',
        data: metricsHistory.map((m) => m.errorRate),
        borderColor: 'rgb(255, 99, 132)',
        backgroundColor: 'rgba(255, 99, 132, 0.2)',
        fill: true,
      },
      {
        label: 'Cache Hit Rate (%)',
        data: metricsHistory.map((m) => m.cacheHitRate),
        borderColor: 'rgb(54, 162, 235)',
        backgroundColor: 'rgba(54, 162, 235, 0.2)',
        fill: true,
      },
    ],
  };

  const errorRateChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
      },
      title: {
        display: true,
        text: 'Error Rate & Cache Performance',
      },
    },
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {isOffline && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          You are currently offline. System health data may be outdated.
        </Alert>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          System Health Monitoring
        </Typography>
        <Tooltip title="Refresh all metrics">
          <IconButton onClick={handleRefresh} disabled={isRefreshing || isOffline}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {isRefreshing && <LinearProgress sx={{ mb: 2 }} />}

      {/* System Overview Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <TimerIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">System Uptime</Typography>
              </Box>
              <Typography variant="h4">{formatUptime(systemUptime)}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <SpeedIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">Avg Response Time</Typography>
              </Box>
              <Typography variant="h4">
                {metricsHistory.length > 0
                  ? Math.round(
                      metricsHistory.reduce((sum, m) => sum + m.responseTime, 0) /
                        metricsHistory.length
                    )
                  : 0}
                ms
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <MemoryIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">Cache Hit Rate</Typography>
              </Box>
              <Typography variant="h4">{redisMetrics.hitRate.toFixed(1)}%</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <StorageIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">Active Shards</Typography>
              </Box>
              <Typography variant="h4">
                {shards.filter((s) => s.status === 'healthy').length}/{shards.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Service Health Status */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h5" gutterBottom>
          Service Health Status
        </Typography>
        <Grid container spacing={2}>
          {services.map((service) => (
            <Grid item xs={12} sm={6} md={3} key={service.name}>
              <Card variant="outlined">
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="h6">{service.name}</Typography>
                    {getStatusIcon(service.status)}
                  </Box>
                  <Chip
                    label={service.status.toUpperCase()}
                    color={getStatusColor(service.status) as any}
                    size="small"
                    sx={{ mb: 1 }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    Response Time: {service.responseTime}ms
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Uptime: {service.uptime}%
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Error Rate: {service.errorRate}%
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Last Check: {format(new Date(service.lastCheck), 'HH:mm:ss')}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Paper>

      {/* Real-time Metrics Charts */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: 400 }}>
            <Line data={metricsChartData} options={metricsChartOptions} />
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: 400 }}>
            <Line data={errorRateChartData} options={errorRateChartOptions} />
          </Paper>
        </Grid>
      </Grid>

      {/* Database Shards Status */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h5" gutterBottom>
          Database Shards Status
        </Typography>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Shard ID</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Users</TableCell>
                <TableCell align="right">Activities</TableCell>
                <TableCell align="right">Size (MB)</TableCell>
                <TableCell>Last Check</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {shards.map((shard) => (
                <TableRow key={shard.id}>
                  <TableCell>{shard.id}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {getStatusIcon(shard.status)}
                      <Chip
                        label={shard.status.toUpperCase()}
                        color={getStatusColor(shard.status) as any}
                        size="small"
                        sx={{ ml: 1 }}
                      />
                    </Box>
                  </TableCell>
                  <TableCell align="right">{shard.userCount.toLocaleString()}</TableCell>
                  <TableCell align="right">{shard.activityCount.toLocaleString()}</TableCell>
                  <TableCell align="right">{shard.sizeMB.toLocaleString()}</TableCell>
                  <TableCell>{format(new Date(shard.lastCheck), 'yyyy-MM-dd HH:mm:ss')}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Celery Workers Status */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h5" gutterBottom>
          Celery Workers Status
        </Typography>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Worker Name</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Tasks Processed</TableCell>
                <TableCell>Current Task</TableCell>
                <TableCell>Last Seen</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {celeryWorkers.map((worker) => (
                <TableRow key={worker.name}>
                  <TableCell>{worker.name}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {getStatusIcon(worker.status)}
                      <Chip
                        label={worker.status.toUpperCase()}
                        color={getStatusColor(worker.status) as any}
                        size="small"
                        sx={{ ml: 1 }}
                      />
                    </Box>
                  </TableCell>
                  <TableCell align="right">{worker.tasksProcessed.toLocaleString()}</TableCell>
                  <TableCell>{worker.currentTask || '-'}</TableCell>
                  <TableCell>{format(new Date(worker.lastSeen), 'yyyy-MM-dd HH:mm:ss')}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Redis Memory Usage */}
      <Paper sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom>
          Redis Memory Usage
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">Memory Usage</Typography>
                <Typography variant="body2">
                  {redisMetrics.memoryUsedMB} / {redisMetrics.memoryMaxMB} MB
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={(redisMetrics.memoryUsedMB / redisMetrics.memoryMaxMB) * 100}
                sx={{ height: 10, borderRadius: 5 }}
              />
            </Box>
            <Typography variant="body2" color="text.secondary">
              Connected Clients: {redisMetrics.connectedClients}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total Keys: {redisMetrics.keysCount.toLocaleString()}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Hit Rate: {redisMetrics.hitRate.toFixed(2)}%
            </Typography>
          </Grid>
        </Grid>
      </Paper>
    </Container>
  );
};

export default SystemHealthPage;